import React, { useRef, useEffect, useState, useCallback } from "react";
import { RoleHelper } from "src/helpers/roleHelper";
import { Data, TableColumn } from "src/types";
import { ColumnHeader } from "src/enums";
import { useAuthorisation } from "src/context";
import { NoDataDisplay } from "..";
import { Table as ShadcnTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/shadcn/ui/table";

export type TableRowHighlight = "recommended" | "urgent" | "success" | "danger" | "info";

const ROW_HIGHLIGHT_CLASSES: Record<TableRowHighlight, { row?: string; stickyCell?: string }> = {
  recommended: { stickyCell: "border-l-2 border-l-blue-500" },
  urgent: { stickyCell: "border-l-2 border-l-amber-500" },
  success: {
    row: "!bg-emerald-50 hover:!bg-emerald-100",
    stickyCell: "!bg-emerald-50 group-hover:!bg-emerald-100"
  },
  danger: {
    row: "!bg-red-50 hover:!bg-red-100",
    stickyCell: "!bg-red-50 group-hover:!bg-red-100"
  },
  info: {
    row: "!bg-blue-50 hover:!bg-blue-100",
    stickyCell: "!bg-blue-50 group-hover:!bg-blue-100"
  }
};

interface TableProps<H extends ColumnHeader, D extends Data> {
  data: D[];
  columns: TableColumn<H, D>[];
  columnsToHide?: H[];
  rowHighlight?: (row: D, rowIndex: number) => TableRowHighlight | undefined;
}

const Table = <H extends ColumnHeader, D extends Data>({
  data,
  columns,
  columnsToHide = [],
  rowHighlight
}: TableProps<H, D>) => {
  const { userRole } = useAuthorisation();
  const emptyCell = " - ";

  const visibleColumns = columns.filter((column) => RoleHelper.isRequiredRole(userRole, column.roleRequired ?? []));

  const headerRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const headerRowRef = useRef<HTMLTableRowElement | null>(null);
  const [stickyLeftOffsets, setStickyLeftOffsets] = useState<number[]>([]);

  const measureStickyColumns = useCallback(() => {
    const offsets: number[] = [];
    let cumulativeLeft = 0;

    visibleColumns.forEach((column, index) => {
      const headerKey =
        typeof column.header === "string" || typeof column.header === "number" ? column.header : undefined;
      if (headerKey && columnsToHide.includes(headerKey as H)) {
        offsets.push(0);
        return;
      }

      if (column.sticky) {
        offsets.push(cumulativeLeft);
        const headerElement = headerRefs.current[index];
        if (headerElement) {
          cumulativeLeft += headerElement.offsetWidth;
        } else {
          const estimatedWidth = (column.styles?.minWidth as number) || (column.styles?.width as number) || 200;
          cumulativeLeft += typeof estimatedWidth === "number" ? estimatedWidth : 200;
        }
      } else {
        offsets.push(0);
      }
    });

    setStickyLeftOffsets(offsets);
  }, [visibleColumns, columnsToHide]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      setTimeout(() => {
        measureStickyColumns();
      }, 10);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [measureStickyColumns, data.length, visibleColumns.length]);

  useEffect(() => {
    if (!headerRowRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      measureStickyColumns();
    });

    resizeObserver.observe(headerRowRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureStickyColumns]);

  useEffect(() => {
    const handleResize = () => {
      measureStickyColumns();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [measureStickyColumns]);

  const getStickyLeft = (columnIndex: number) => {
    return stickyLeftOffsets[columnIndex] ?? 0;
  };

  return data.length === 0 ? (
    <div className="flex items-center justify-center py-20 px-4">
      <NoDataDisplay />
    </div>
  ) : (
    <div className="rounded-xl border border-secondary-200 bg-background shadow-soft overflow-hidden">
      <div className="overflow-x-auto">
        <ShadcnTable>
          <TableHeader className="top-0 z-10 bg-secondary-900">
            <TableRow
              ref={headerRowRef}
              className="bg-secondary-900 hover:bg-secondary-900 border-b-2 border-secondary-800"
            >
              {visibleColumns.map((column, index) => {
                const headerKey =
                  typeof column.header === "string" || typeof column.header === "number" ? column.header : undefined;
                const isHidden = headerKey ? columnsToHide.includes(headerKey as H) : false;

                return (
                  !isHidden && (
                    <TableHead
                      key={index}
                      ref={(el) => {
                        headerRefs.current[index] = el;
                      }}
                      style={{
                        ...column.styles,
                        ...(column.sticky ? { left: `${getStickyLeft(index)}px` } : {})
                      }}
                      className={`h-14 px-6 text-left align-middle font-bold text-background text-sm tracking-wide uppercase [&:has([role=checkbox])]:pr-0 ${
                        column.sticky ? `sticky top-0 z-20 bg-secondary-900` : ""
                      }`}
                    >
                      {column.header}
                    </TableHead>
                  )
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => {
              const highlight = rowHighlight?.(row, rowIndex);
              const styles = highlight ? ROW_HIGHLIGHT_CLASSES[highlight] : undefined;
              const rowClass = `group border-b border-secondary-100 transition-colors hover:bg-secondary-100 data-[state=selected]:bg-secondary-100 ${styles?.row ?? ""}`;
              const stickyCellBgClass = `bg-background group-hover:bg-secondary-100 ${styles?.stickyCell ?? ""}`;

              return (
                <TableRow key={rowIndex} className={rowClass}>
                  {visibleColumns.map((column, columnIndex) => {
                    const headerKey =
                      typeof column.header === "string" || typeof column.header === "number"
                        ? column.header
                        : undefined;
                    const isHidden = headerKey ? columnsToHide.includes(headerKey as H) : false;
                    if (isHidden) return null;
                    return (
                      <TableCell
                        key={columnIndex}
                        style={{
                          ...column.styles,
                          ...(column.sticky ? { left: `${getStickyLeft(columnIndex)}px` } : {})
                        }}
                        className={`px-6 py-4 align-middle text-sm text-foreground [&:has([role=checkbox])]:pr-0 ${
                          column.sticky ? `sticky z-10 ${stickyCellBgClass}` : ""
                        }`}
                      >
                        {column.column(row) ?? <span className="text-muted-500">{emptyCell}</span>}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </ShadcnTable>
      </div>
    </div>
  );
};

export { Table };
